import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Button,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Post } from '../../../Axios/AxiosFunctions';
import { BaseURL, apiHeader } from '../../../config/apiUrl';
import Loader from '../../../Component/Loader';
import { formatPhoneNumber } from '../../../utils/functions';

const validationSchema = yup.object().shape({
  content: yup.string().required('Message is required'),
});

export default function SendSmsDialog({ open, onClose, contact }) {
    const { access_token: accessToken, user } = useSelector(
      (state) => state?.authReducer || {},
    );

    const [loading, setLoading] = useState(false);
    const [banner, setBanner] = useState(null); // { severity, text } for the inline banner

    const formik = useFormik({
      initialValues: {
        content: '',
      },
      validationSchema,
      onSubmit: (values) => {
        setLoading(true);
        setBanner(null);
        const { content } = values;

        // Get phone number
        const phoneNumber = contact?.contact || contact?.phone || contact?.secondaryPhone || contact?.alternativePhone;

        if (!phoneNumber) {
          setBanner({ severity: 'warning', text: 'This contact has no phone number on file.' });
          setLoading(false);
          return;
        }

        // Format phone number (ensure it starts with +)
        const to = phoneNumber[0] === '+' ? phoneNumber : `+1${phoneNumber.replace(/\D/g, '')}`;
        
        // Call Zoom SMS API
        Post(
          BaseURL('zoom/sms/send'),
          {
            message: content,
            phone_numbers: [to],
            userId: user?._id || user?.id,  // CRM user ID for Zoom token
          },
          apiHeader(accessToken),
        )
          .then((res) => {
            setLoading(false);
            if (!res.data?.success) {
              // Handle auth errors
              if (res.data?.requiresAuth || res.data?.requiresReauth) {
                return setBanner({ severity: 'warning', text: res.data?.message || 'Please connect your Zoom account first.' });
              }
              if (res.data?.noZoomPhone) {
                return setBanner({ severity: 'info', text: res.data.message });
              }
              return setBanner({ severity: 'error', text: res.data?.message || 'Failed to send SMS.' });
            }
            formik.resetForm();
            onClose();
            return toast.success('SMS sent successfully via Zoom!');
          })
          .catch((err) => {
            setLoading(false);
            const data = err?.response?.data;
            const errorMsg =
              (typeof data === 'object' ? data?.message : data) || err.toString();
            const statusCode = err?.response?.status;

            // Expected case: the connected Zoom account has no Zoom Phone line
            if (data?.noZoomPhone) {
              return setBanner({ severity: 'info', text: data.message });
            }

            // Check if user needs to connect Zoom (401 means not authenticated with Zoom)
            if (statusCode === 401 || (typeof errorMsg === 'string' && (errorMsg.includes('not authenticated') || errorMsg.includes('connect Zoom')))) {
              return setBanner({ severity: 'warning', text: 'Please connect your Zoom account to send SMS.' });
            }

            return setBanner({ severity: 'error', text: errorMsg });
          });
      },
    });

    const contactName = `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() || 'Unknown';
    const phoneNumber = contact?.contact || contact?.phone || contact?.secondaryPhone || contact?.alternativePhone || '';

    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 1300 }}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Send SMS to {contactName}</Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {loading && <Loader />}
          <Stack spacing={2}>
            {banner && (
              <Alert
                severity={banner.severity}
                onClose={() => setBanner(null)}
                sx={{ whiteSpace: 'pre-line' }}
              >
                {banner.text}
              </Alert>
            )}

            <TextField
              label="To Phone"
              fullWidth
              size="small"
              value={formatPhoneNumber(phoneNumber) || phoneNumber}
              InputProps={{ readOnly: true }}
            />

            <TextField
              name="content"
              label="Message"
              placeholder="Type your SMS message here..."
              multiline
              minRows={4}
              fullWidth
              value={formik.values.content}
              onChange={formik.handleChange}
              error={formik.touched.content && !!formik.errors.content}
              helperText={formik.touched.content && formik.errors.content}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={formik.submitForm}
            variant="contained"
            disabled={loading || !phoneNumber}
          >
            Send SMS
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
